import { AblyManager, AckData, SendMessageParams, GroupManagementEvent } from './ably-manager';
import { GroupNotificationMapper } from './group-notification-mapper';
import { MessageInfo, MessageContentData, Attachment, MessageType, MessageStatus, SessionType, AttachmentType, AtUserAttachment, AttachmentDownloadStatus, UploadStatus } from '@/api/types/chat';
import { useChatStore } from '@/stores/useChatStore';
import { useGroupStore } from '@/stores/useGroupStore';
import { tabStateManager } from '@/utils/tabStateManager';
import { logPrint } from '../my-utils';
import { GroupMember } from '@/api/types/group';

/**
 * 基础消息参数
 */
export interface BaseMessageParams {
  conversationId: string;
  receiverId?: string;
  receiverHandle?: number;
  sessionType: SessionType;
}

/**
 * 文本消息参数
 */
export interface TextParams extends BaseMessageParams {
  content: string;
  translateContent?: string;
}

export interface TextAtParams extends BaseMessageParams {
  content: string;
  translateContent?: string;
  userList?: GroupMember[];
  isAll: boolean;
}

/**
 * 图片消息参数（简化版）
 */
export interface ImageParams extends BaseMessageParams {
  attachment: Attachment;
}

/**
 * 语音消息参数（简化版）
 */
export interface VoiceParams extends BaseMessageParams {
  attachment: Attachment;
}

/**
 * 视频消息参数（简化版）
 */
export interface VideoParams extends BaseMessageParams {
  attachment: Attachment;
}

/**
 * 文件消息参数（简化版）
 */
export interface FileParams extends BaseMessageParams {
  attachment: Attachment;
}

/**
 * 联系人消息参数
 */
export interface ContactParams extends BaseMessageParams {
  contactInfo: {
    id: string;
    name: string;
    avatar?: string;
    phone?: string;
    handle?: string;
  };
}

/**
 * 消息管理器
 * 负责消息的发送、接收、加密解密和状态管理
 */
export class MessageManager {
  private static instance: MessageManager;
  private ablyManager: AblyManager;
  private isInitialized = false;

  private constructor() {
    this.ablyManager = AblyManager.getInstance();
  }

  /**
   * 获取单例实例
   */
  static getInstance(): MessageManager {
    if (!MessageManager.instance) {
      MessageManager.instance = new MessageManager();
    }
    return MessageManager.instance;
  }

  /**
   * 初始化消息管理器
   */
  async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      console.log('消息管理器已初始化');
      return true;
    }

    try {
      // 初始化 Ably 连接
      const success = await this.ablyManager.initialize();
      if (!success) {
        throw new Error('Ably 连接初始化失败');
      }

      this.isInitialized = true;
      console.log('消息管理器初始化成功');
      return true;
    } catch (error) {
      console.error('消息管理器初始化失败:', error);
      return false;
    }
  }

  /**
   * 订阅现有群组频道
   */
  public async subscribeToExistingGroups(): Promise<void> {
    try {
      const groupMap = useGroupStore.getState().groupMap;

      // 为所有群组订阅频道
      const groupIds = Array.from(groupMap.values()).map(group => group.id);
      for (const groupId of groupIds) {
        await this.subscribeToGroup(groupId);
      }
    } catch (error) {
      console.error('订阅现有群组失败:', error);
    }
  }

  /**
  * 订阅群组频道
  */
  async subscribeToGroup(groupId: string): Promise<void> {
    return await this.ablyManager.subscribeToGroupChannel(groupId);
  }

  /**
   * 取消订阅群组频道
   */
  async unsubscribeFromGroup(groupId: string): Promise<void> {
    await this.ablyManager.unsubscribeFromGroupChannel(groupId);
  }

  /**
   * 发送文本消息
   */
  async sendText(params: TextParams): Promise<boolean> {
    return this.sendMessageWithType({
      ...params,
      messageType: MessageType.TEXT,
      content: params.content,
      translateContent: params.translateContent,
    });
  }

  /**
   * 发送图片消息（简化版，直接传入 Attachment）
   */
  async sendImage(params: ImageParams): Promise<boolean> {
    return this.sendMessageWithType({
      ...params,
      messageType: MessageType.IMAGE,
      content: `[图片] ${params.attachment.fileName}`,
      attachment: params.attachment
    });
  }

  /**
   * 发送语音消息（简化版，直接传入 Attachment）
   */
  async sendVoice(params: VoiceParams): Promise<boolean> {
    return this.sendMessageWithType({
      ...params,
      messageType: MessageType.VOICE,
      content: `[语音] ${Math.round(params.attachment.duration || 0)}秒`,
      attachment: params.attachment
    });
  }

  /**
   * 发送视频消息（简化版，直接传入 Attachment）
   */
  async sendVideo(params: VideoParams): Promise<boolean> {
    return this.sendMessageWithType({
      ...params,
      messageType: MessageType.VIDEO,
      content: `[视频] ${params.attachment.fileName}`,
      attachment: params.attachment
    });
  }

  /**
   * 发送文件消息（简化版，直接传入 Attachment）
   */
  async sendFile(params: FileParams): Promise<boolean> {
    return this.sendMessageWithType({
      ...params,
      messageType: MessageType.FILE,
      content: `[文件] ${params.attachment.fileName}`,
      attachment: params.attachment
    });
  }

  /**
   * 发送联系人消息
   */
  async sendContact(params: ContactParams): Promise<boolean> {
    return this.sendMessageWithType({
      ...params,
      messageType: MessageType.CONTACT,
      content: `[名片] ${params.contactInfo.name}`,
      attachment: {
        url: '',
        fileName: '',
        fileSize: 0,
        fileExtension: '',
        type: AttachmentType.CONTACT,
        uploadStatus: null,
        autoDownload: false,
        width: null,
        height: null,
        samples: [],
        duration: 0,
        contactInfo: params.contactInfo,
        fileId: '',
        thumbnail: null,
        downloadStatus: null,
        atUsers: null,
      }
    });
  }

  /**
   * 发送 @ 消息
   */
  async sendTextAt(params: TextAtParams): Promise<boolean> {
    const atUsers = params.userList?.map(user => ({
      id: user.id,
      name: user.nickname,
      avatar: user.avatar,
      handle: user.handle.toString(),
      isAll: params.isAll
    } as unknown as AtUserAttachment));

    const attachment = this.createAtAttachment();
    attachment.type = AttachmentType.AT;
    attachment.atUsers = atUsers || null;

    return this.sendMessageWithType({
      ...params,
      messageType: MessageType.AT,
      content: params.content,
      translateContent: params.translateContent,
      attachment: attachment
    });
  }

  /**
   * 内部发送消息核心方法
   */
  private async sendMessageWithType(params: {
    conversationId: string;
    receiverId?: string;
    receiverHandle?: number;
    sessionType: SessionType;
    messageType: MessageType;
    content: string;
    translateContent?: string;
    attachment?: Attachment;
  }): Promise<boolean> {
    try {
      

      // 获取当前用户信息
      const authData = tabStateManager.getAuthData();
      if (!authData?.userInfo?.handle) {
        throw new Error('无法获取当前用户信息');
      }

      let groupId = undefined;
      if (params.sessionType === SessionType.Group) {
        groupId = useGroupStore.getState().groupMap.get(params.conversationId)?.id;
      }

      // 创建消息内容对象
      const messageContentData: MessageContentData = {
        id: crypto.randomUUID(),
        content: params.translateContent ?? params.content,
        status: MessageStatus.PENDING,
        senderId: authData.userId || '',
        receiverId: (params.sessionType === SessionType.Group ? groupId : params.receiverId) || '',
        receiveHandle: params.receiverHandle?.toString() || '0',
        senderName: authData.userInfo?.nickname || 'Unknown',
        timestamp: new Date().toISOString(),
        attachment: params.attachment || undefined,
        conversationId: params.conversationId,
        messageType: params.messageType,
        showTranslate: false,
        translateContent: params.content || '', // 先用 translateContent 来储存原始的 未翻译内容
      };

      // 构造发送数据
      const sendData: SendMessageParams = {
        conversationId: params.conversationId,
        groupId: groupId,
        receiverId: params.receiverId,
        receiverHandle: params.receiverHandle,
        contentData: messageContentData,
        sessionType: params.sessionType
      };

      logPrint('发送消息: sendData: ', sendData);

      // 通过 Ably 发送消息
      const success = await this.ablyManager.sendMessage(sendData);

      if (success) {
        // 创建本地消息记录
        const localMessage: MessageInfo = {
          message_id: messageContentData.id,
          conversation_id: params.conversationId,
          sender_id: authData.userId || '',
          receiver_id: params.receiverId,
          message_type: this.getMessageTypeNumber(params.messageType),
          content: JSON.stringify(messageContentData),
          iv: '', // 实际应该包含加密 IV
          auth_tag: '', // 实际应该包含认证标签
          key_id: '',
          message_id_time: null,
          receiver_time: Date.now(),
          deleted_at: null,
          attachments: [],
          messageType: params.messageType,
          ack: false,
          read: false,
          decryptedContentData: messageContentData,
          senderName: '我',
          senderAvatar: authData.userInfo?.avatar
        };

        // 添加到本地状态
        const chatStore = useChatStore.getState();
        chatStore.addMessage(params.conversationId, localMessage);

        console.log('消息发送成功');
        return true;
      } else {
        throw new Error('消息发送失败');
      }
    } catch (error) {
      console.error('发送消息失败:', error);
      return false;
    }
  }

  /**
   * 将消息类型字符串转换为数字
   * 这个目前没什么用，前后端都不会根据这个类型判断任何东西
   */
  private getMessageTypeNumber(messageType: string): number {
    const typeMap: Record<string, number> = {
      // 基础消息类型
      'TEXT': 1,
      'IMAGE': 2,
      'VOICE': 3,
      'VIDEO': 4,
      'FILE': 5,
      'CONTACT': 6,
      'AT': 7,
    };
    return typeMap[messageType] || 1;
  }

  /**
   * 确认消息已读
   */
  async ackMessages(ackDatas: AckData[]): Promise<boolean> {
    try {

      const success = await this.ablyManager.sendAckMessages(ackDatas);

      if (success) {
        // 本地也更新状态
        const chatStore = useChatStore.getState();
        const messageIds = ackDatas.map(ackData => ackData.message_id);
        chatStore.markMessagesAsRead(ackDatas[0].conversation_id, messageIds);
        console.log('消息确认发送成功');
        return true;
      } else {
        throw new Error('消息确认发送失败');
      }
    } catch (error) {
      console.error('确认消息失败:', error);
      return false;
    }
  }



  /**
   * 发送群组创建通知
   */
  async sendGroupCreateNotification(conversationId: string, groupId: string): Promise<boolean> {
    const authData = tabStateManager.getAuthData();
    if (!authData?.userInfo) {
      return false;
    }

    return this.sendGroupMessageNotification(
      conversationId,
      MessageType.GROUP_NEW_CREATE,
      JSON.stringify({
        sender_name: authData.userInfo.nickname || 'Unknown',
        group_id: groupId
      })
    );
  }

  /**
   * 发送群组邀请通知
   */
  async sendGroupInviteNotification(
    conversationId: string,
    memberHandles: number[],
    memberNames: string[]
  ): Promise<boolean> {
    const authData = tabStateManager.getAuthData();
    if (!authData?.userInfo) {
      return false;
    }

    return this.sendGroupMessageNotification(
      conversationId,
      MessageType.GROUP_NEW_INVITE,
      JSON.stringify({
        member_handles: memberHandles,
        member_names: memberNames,
        invite_id: authData.userId,
        invite_name: authData.userInfo.nickname || 'Unknown'
      })
    );
  }

  /**
   * 发送群组信息更新通知
   */
  async sendGroupInfoUpdateNotification(
    conversationId: string,
    updateType: 'name' | 'avatar' | 'color',
    newValue: string
  ): Promise<boolean> {
    const authData = tabStateManager.getAuthData();
    if (!authData?.userInfo) {
      return false;
    }

    const content: any = {
      sender_name: authData.userInfo.nickname || 'Unknown',
      group_update_type: updateType
    };

    switch (updateType) {
      case 'name':
        content.group_name = newValue;
        break;
      case 'avatar':
        content.group_avatar = newValue;
        break;
      case 'color':
        content.group_color = newValue;
        break;
    }

    return this.sendGroupMessageNotification(
      conversationId,
      MessageType.GROUP_INFO_UPDATE,
      JSON.stringify(content)
    );
  }

  /**
   * 发送群组成员添加通知
   */
  async sendGroupMemberAddedNotification(
    conversationId: string,
    memberHandles: number[],
    memberNames: string[],
  ): Promise<boolean> {
    const authData = tabStateManager.getAuthData();
    if (!authData?.userInfo) {
      return false;
    }

    return this.sendGroupMessageNotification(
      conversationId,
      MessageType.GROUP_MEMBER_ADDED,
      JSON.stringify({
        member_handles: memberHandles,
        member_names: memberNames,
        invite_id: authData.userId,
        invite_name: authData.userInfo.nickname || 'Unknown'
      })
    );
  }

  /**
   * 发送群组成员移除通知
   */
  async sendGroupMemberRemovedNotification(
    conversationId: string,
    memberHandles: number[],
    memberNames: string[]
  ): Promise<boolean> {
    const authData = tabStateManager.getAuthData();
    if (!authData?.userInfo) {
      return false;
    }

    return this.sendGroupMessageNotification(
      conversationId,
      MessageType.GROUP_MEMBER_REMOVED,
      JSON.stringify({
        member_handles: memberHandles,
        member_names: memberNames,
        sender_id: authData.userId,
        sender_name: authData.userInfo.nickname || 'Unknown'
      })
    );
  }

  /**
   * 发送群组解散通知
   */
  async sendGroupDissolveNotification(conversationId: string): Promise<boolean> {
    const authData = tabStateManager.getAuthData();
    if (!authData?.userInfo) {
      return false;
    }

    return this.sendGroupMessageNotification(
      conversationId,
      MessageType.GROUP_DISSOLVE,
      JSON.stringify({
        sender_id: authData.userId,
        sender_name: authData.userInfo.nickname || 'Unknown'
      })
    );
  }

  /**
   * 发送群组成员离开通知
   */
  async sendGroupMemberLeaveNotification(conversationId: string): Promise<boolean> {
    const authData = tabStateManager.getAuthData();
    if (!authData?.userInfo) {
      return false;
    }

    return this.sendGroupMessageNotification(
      conversationId,
      MessageType.GROUP_MEMBER_LEAVE,
      JSON.stringify({
        sender_id: authData.userId,
        sender_name: authData.userInfo.nickname || 'Unknown'
      })
    );
  }

  /**
   * 发送群组公告添加通知
   */
  async sendGroupNoticeAddNotification(
    conversationId: string,
    notice: string
  ): Promise<boolean> {
    const authData = tabStateManager.getAuthData();
    if (!authData?.userInfo) {
      return false;
    }

    return this.sendGroupMessageNotification(
      conversationId,
      MessageType.GROUP_NOTICE_ADD,
      JSON.stringify({
        notice: notice,
        sender_name: authData.userInfo.nickname || 'Unknown'
      })
    );
  }

  /**
   * 发送群组置顶消息通知
   */
  async sendGroupPinnedMessageNotification(
    conversationId: string,
    messageId: string,
    isPinned: boolean
  ): Promise<boolean> {
    const authData = tabStateManager.getAuthData();
    if (!authData?.userInfo) {
      return false;
    }

    return this.sendGroupMessageNotification(
      conversationId,
      isPinned ? MessageType.GROUP_PINNED_MESSAGE_ADD : MessageType.GROUP_PINNED_MESSAGE_DELETE,
      JSON.stringify({
        message_id: messageId,
        sender_name: authData.userInfo.nickname || 'Unknown'
      })
    );

  }

  /**
   * 发送群组禁言状态通知
   */
  async sendGroupMuteNotification(
    conversationId: string,
    isEnabled: boolean,
    permissions: string
  ): Promise<boolean> {
    const authData = tabStateManager.getAuthData();
    if (!authData?.userInfo) {
      return false;
    }

    return this.sendGroupMessageNotification(
      conversationId,
      isEnabled ? MessageType.GROUP_MUTE_ENABLED : MessageType.GROUP_MUTE_DISABLED,
      JSON.stringify({
        sender_name: authData.userInfo.nickname || 'Unknown',
        permissions: permissions
      })
    );
  }

  /**
   * 发送群组截图权限通知
   */
  async sendGroupScreenshotNotification(
    conversationId: string,
    isEnabled: boolean,
    permissions: string
  ): Promise<boolean> {
    const authData = tabStateManager.getAuthData();
    if (!authData?.userInfo) {
      return false;
    }

    return this.sendGroupMessageNotification(
      conversationId,
      isEnabled ? MessageType.GROUP_SCREENSHOT_ENABLED : MessageType.GROUP_SCREENSHOT_DISABLED,
      JSON.stringify({
        sender_name: authData.userInfo.nickname || 'Unknown',
        permissions: permissions
      })
    );

  }

  /**
   * 发送群组媒体发送权限通知
   */
  async sendGroupSendMediaNotification(
    conversationId: string,
    mediaType: 'card' | 'image' | 'video' | 'file',
    isEnabled: boolean,
    permissions: string
  ): Promise<boolean> {
    const authData = tabStateManager.getAuthData();
    if (!authData?.userInfo) {
      return false;
    }

    const messageType = this.getMediaMessageType(mediaType, isEnabled);

    return this.sendGroupMessageNotification(
      conversationId,
      messageType,
      JSON.stringify({
        sender_name: authData.userInfo.nickname || 'Unknown',
        permissions: permissions
      })
    );
  }

  /**
   * 根据媒体类型和状态获取对应的消息类型
   */
  private getMediaMessageType(mediaType: 'card' | 'image' | 'video' | 'file', isEnabled: boolean): MessageType {
    switch (mediaType) {
      case 'card':
        return isEnabled ? MessageType.GROUP_SEND_CARD_ENABLED : MessageType.GROUP_SEND_CARD_DISABLED;
      case 'image':
        return isEnabled ? MessageType.GROUP_SEND_IMAGE_ENABLED : MessageType.GROUP_SEND_IMAGE_DISABLED;
      case 'video':
        return isEnabled ? MessageType.GROUP_SEND_VIDEO_ENABLED : MessageType.GROUP_SEND_VIDEO_DISABLED;
      case 'file':
        return isEnabled ? MessageType.GROUP_SEND_FILE_ENABLED : MessageType.GROUP_SEND_FILE_DISABLED;
      default:
        return MessageType.GROUP_SEND_CARD_ENABLED;
    }
  }


  /**
   * 发送群组管理通知（不加密）
   * 用于群组订阅管理等操作
   */
  async sendGroupManagementNotification(
    groupId: string,
    eventType: GroupManagementEvent,
    eventData: Record<string, any>,
    userHandles?: number[]
  ): Promise<boolean> {
    try {
      const success = await this.ablyManager.sendGroupManagementNotification(
        groupId,
        eventType,
        eventData,
        userHandles
      );

      if (success) {
        console.log('群组管理通知发送成功');
        return true;
      } else {
        throw new Error('群组管理通知发送失败');
      }
    } catch (error) {
      console.error('发送群组管理通知失败:', error);
      return false;
    }
  }

  /**
   * 发送群组消息通知（加密，显示在消息记录中）
   */
  private async sendGroupMessageNotification(
    conversationId: string,
    messageType: MessageType,
    content: string
  ): Promise<boolean> {
    try {
      const authData = tabStateManager.getAuthData();
      if (!authData?.userInfo) {
        throw new Error('无法获取当前用户信息');
      }

      // 获取对应的群组消息事件类型
      const eventType = GroupNotificationMapper.getGroupMessageEvent(messageType);
      if (!eventType) {
        throw new Error(`不支持的群组消息类型: ${messageType}`);
      }

      const groupId = useGroupStore.getState().groupMap.get(conversationId)?.id;
      // 创建标准的 MessageContentData 对象
      const messageContent: MessageContentData = {
        id: crypto.randomUUID(),
        content: content,
        status: MessageStatus.SENT,
        senderId: authData.userId || '',
        receiverId: groupId || conversationId,
        senderName: authData.userInfo.nickname || 'System',
        timestamp: new Date().toISOString(),
        attachment: null,
        receiveHandle: '0',
        conversationId: conversationId,
        messageType: messageType,
        showTranslate: false,
        translateContent: '',
      };

      // 构造发送数据（类似普通消息）
      const sendData: SendMessageParams = {
        conversationId: conversationId,
        groupId: groupId,
        contentData: messageContent,
        sessionType: SessionType.Group
      };

      // 使用群组消息通知发送方法（传递事件类型和加密的消息数据）
      const success = await this.ablyManager.sendGroupMessageNotification(sendData, eventType);

      if (success) {
        // 创建本地消息记录
        const localMessage: MessageInfo = {
          message_id: messageContent.id,
          conversation_id: conversationId,
          sender_id: authData.userId || '',
          receiver_id: groupId,
          message_type: this.getMessageTypeNumber(messageType),
          content: JSON.stringify(messageContent),
          iv: '',
          auth_tag: '',
          key_id: '',
          message_id_time: null,
          receiver_time: Date.now(),
          deleted_at: null,
          attachments: [],
          messageType: messageType,
          ack: false,
          read: false,
          decryptedContentData: messageContent,
          senderName: authData.userInfo.nickname || 'System',
          senderAvatar: authData.userInfo?.avatar
        };

        // 添加到本地状态
        const chatStore = useChatStore.getState();
        chatStore.addMessage(conversationId, localMessage);

        console.log('群组消息通知发送成功');
        return true;
      } else {
        throw new Error('群组消息通知发送失败');
      }
    } catch (error) {
      console.error('发送群组消息通知失败:', error);
      return false;
    }
  }

  /**
   * 创建空的附件参数
   * @returns 因为空附件不能发送，所以创建一个空的附件参数，再修改内部具体需要的数值
   */
  private createAtAttachment(): Attachment {
    return {
      url: '',
      fileName: '',
      fileSize: 0,
      fileExtension: '',
      type: AttachmentType.AT,
      uploadStatus: UploadStatus.UPLOADED,
      autoDownload: false,
      width: null,
      height: null,
      samples: null,
      duration: null,
      contactInfo: null,
      fileId: null,
      thumbnail: null,
      downloadStatus: AttachmentDownloadStatus.NOT_DOWNLOADING,
      atUsers: null
    } as Attachment;
  }

  /**
   * 获取连接状态
   */
  isConnected(): boolean {
    return this.ablyManager.getConnectionStatus();
  }

  /**
   * 重新连接
   */
  async reconnect(): Promise<boolean> {
    console.log('重新连接消息管理器...');
    return await this.ablyManager.reconnect();
  }

  /**
   * 断开连接
   */
  async disconnect(): Promise<void> {
    await this.ablyManager.disconnect();
    this.isInitialized = false;
    console.log('消息管理器已断开连接');
  }
}

export default MessageManager;
